import { Injectable, inject } from '@angular/core';
import { environment } from '@eview/core/environments/environment';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { delay, switchMap, tap } from 'rxjs/operators';
import { EAppActions, UserLoggedIn } from '../actions/app.actions';
import {
  GetPermissions,
  GetRoles
} from '../actions/auth.actions';
import { clearLegacyAppStorage } from '../../storage/app-storage.helpers';
import { ListFormAttributes } from '../actions/form-attributes.actions';
import { StartNotifications } from '../actions/notifications.actions';
import { GetUserExtra } from '../actions/user-extra.actions';
import { AuthEffects } from './auth.effects';
import { ConfigEffects } from './config.effects';
import { FormAttributesEffects } from './form-attributes.effects';
import { MapEffects } from './map.effects';
import { NotificationsEffects } from './notifications.effects';
import { PermissionsEffects } from './permissions.effects';
import { RolesEffects } from './roles.effects';
import { TagsEffects } from './tags.effects';
import { UserExtraEffects } from './user-extra.effects';
import { NetworkStatusEffects } from '@eview/core/store/effects/network-status.effects';
import { ExportEffects } from './export.effects';
import { FormsEffects } from './forms.effects';
import { ListForms } from '../actions/forms.actions';
import { TenantEffects } from './tenant.effects';
import { PostEffects } from './posts.effects';
import { ContactUsEffects } from './contact-us.effects';
import { GetAlertsEffects } from './get-alerts.effects';
import { CommentsEffects } from './comment.effects';
import { DashboardEffects } from './dashboard.effects';

@Injectable()
export class AppEffects {
  private readonly actions$ = inject(Actions);

  /** Clear stale local storage; menus unlock from /users/me alone. */
  userLoggedInStorage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<UserLoggedIn>(EAppActions.UserLoggedIn),
        tap(() => clearLegacyAppStorage())
      ),
    { dispatch: false }
  );

  /** Roles/permissions catalogs for admin UIs — not on the critical path. */
  userLoggedInCatalog$ = createEffect(() =>
    this.actions$.pipe(
      ofType<UserLoggedIn>(EAppActions.UserLoggedIn),
      delay(0),
      switchMap(() => [new GetPermissions(true), new GetRoles(true)])
    )
  );

  /** Secondary catalogs after first paint. */
  userLoggedInSecondary$ = createEffect(() =>
    this.actions$.pipe(
      ofType<UserLoggedIn>(EAppActions.UserLoggedIn),
      delay(400),
      switchMap(() => [
        new ListFormAttributes({ id: environment.form.id }),
        new GetUserExtra(),
        new ListForms(),
        new StartNotifications()
      ])
    )
  );
}

export const appEffects = [
  AppEffects,
  AuthEffects,
  ConfigEffects,
  RolesEffects,
  PermissionsEffects,
  MapEffects,
  ContactUsEffects,
  GetAlertsEffects,
  TagsEffects,
  FormAttributesEffects,
  NotificationsEffects,
  UserExtraEffects,
  NetworkStatusEffects,
  ExportEffects,
  FormsEffects,
  TenantEffects,
  PostEffects,
  CommentsEffects,
  DashboardEffects
];
